// ============================================================
// THE COMPANY — Auth Gate (password entry screen)
// ============================================================

import { useState, useCallback, type FormEvent } from "react";
import { useCompanyStore } from "../store/useCompanyStore";

export default function AuthGate() {
  const authenticate = useCompanyStore((s) => s.authenticate);

  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      if (!password.trim() || loading) return;

      setError("");
      setLoading(true);

      const success = await authenticate(password);

      if (!success) {
        setError("Wrong password");
        setLoading(false);
      }
      // If success, the store sets isAuthenticated and App re-renders
    },
    [password, loading, authenticate],
  );

  return (
    <div
      className="flex h-screen w-screen items-center justify-center"
      style={{ backgroundColor: "var(--bg-base)" }}
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-8 px-6">
        {/* Company name */}
        <div className="flex flex-col items-center gap-2">
          <h1
            className="text-2xl font-bold tracking-widest"
            style={{ color: "var(--text-primary)" }}
          >
            THE COMPANY
          </h1>
          <p
            className="text-xs tracking-wide"
            style={{ color: "var(--text-muted)" }}
          >
            Board of Directors Access
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3">
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError("");
            }}
            autoFocus
            className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all duration-150"
            style={{
              backgroundColor: "var(--bg-surface)",
              borderColor: error
                ? "var(--status-blocked)"
                : "var(--border-default)",
              color: "var(--text-primary)",
            }}
          />

          {/* Error message */}
          {error && (
            <p
              className="text-xs fade-in"
              style={{ color: "var(--status-blocked)" }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !password.trim()}
            className="flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: loading
                ? "var(--accent-hover)"
                : "var(--accent)",
            }}
            onMouseEnter={(e) => {
              if (!loading)
                e.currentTarget.style.backgroundColor = "var(--accent-hover)";
            }}
            onMouseLeave={(e) => {
              if (!loading)
                e.currentTarget.style.backgroundColor = "var(--accent)";
            }}
          >
            {loading ? (
              <div
                className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"
                style={{ borderColor: "white", borderTopColor: "transparent" }}
              />
            ) : (
              "Enter"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
